import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import PoiEditor from "./PoiEditor";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import MapView from "./MapView";
import {
  findNearestGpxPointIndex,
  flattenSequencedGpxFiles,
  normalizeGpxPointsInput,
  sliceGpxSubset,
} from "../lib/gpx";

function resequenceGpxFiles(gpxFiles = []) {
  return gpxFiles.map((file, index) => ({
    ...file,
    sequence: index + 1,
  }));
}

export default function PlaylistForm({
  value,
  onChange,
  routes,
  routeContextPoints = [],
  routeDisabled = false,
  youtubePlaylistUrlDisabled = false,
  statusOptions = ["created", "ready"],
  showStatusField = false,
  showCreatorFields = false,
  creators = [],
  gpxUploadMode = "single",
  parseUploadedGpx,
  parseUploadedGpxFiles,
  fetchPlaylistMetadata,
  allowRouteSubsetCopy = false,
}) {
  const v = value || {};
  const set = (key, nextValue) => onChange({ ...v, [key]: nextValue });
  const [isParsingGpx, setIsParsingGpx] = useState(false);
  const [isFetchingPlaylistMetadata, setIsFetchingPlaylistMetadata] =
    useState(false);
  const [lastGpxFile, setLastGpxFile] = useState("");
  const [subsetStartIndex, setSubsetStartIndex] = useState(null);
  const [subsetEndIndex, setSubsetEndIndex] = useState(null);

  const selectedRoute = routes.find((route) => route.id === v.route_id);
  const routePoints =
    normalizeGpxPointsInput(routeContextPoints).length > 0
      ? normalizeGpxPointsInput(routeContextPoints)
      : normalizeGpxPointsInput(
          selectedRoute?.gpx_points || selectedRoute?.gpxPoints || [],
        );
  const playlistOwnGpxPoints = normalizeGpxPointsInput(
    v.gpx_points || v.gpxPoints || [],
  );
  const playlistFilePoints = useMemo(
    () => flattenSequencedGpxFiles(v.gpx_files || []),
    [v.gpx_files],
  );
  const playlistDisplayPoints =
    playlistFilePoints.length > 0
      ? playlistFilePoints
      : playlistOwnGpxPoints.length > 0
        ? playlistOwnGpxPoints
        : routePoints;
  const hasPlaylistOwnGpx = playlistOwnGpxPoints.length > 1;

  useEffect(() => {
    setSubsetStartIndex(null);
    setSubsetEndIndex(null);
  }, [v?.id, v?.route_id]);

  const selectedSubsetSegment = useMemo(() => {
    if (
      subsetStartIndex == null ||
      subsetEndIndex == null ||
      !Array.isArray(routePoints) ||
      routePoints.length === 0
    ) {
      return null;
    }

    const startPoint = routePoints[subsetStartIndex];
    const endPoint = routePoints[subsetEndIndex];
    if (!Array.isArray(startPoint) || !Array.isArray(endPoint)) return null;

    return {
      start: [Number(startPoint[0]) || 0, Number(startPoint[1]) || 0],
      end: [Number(endPoint[0]) || 0, Number(endPoint[1]) || 0],
    };
  }, [subsetStartIndex, subsetEndIndex, routePoints]);

  const handleSubsetMapPick = ({ lat, lng }) => {
    if (
      hasPlaylistOwnGpx ||
      !allowRouteSubsetCopy ||
      !Array.isArray(routePoints) ||
      routePoints.length < 2
    ) {
      return;
    }

    const index = findNearestGpxPointIndex(
      routePoints,
      Number(lat) || 0,
      Number(lng) || 0,
    );
    if (index < 0) return;

    if (
      subsetStartIndex == null ||
      (subsetStartIndex != null && subsetEndIndex != null)
    ) {
      setSubsetStartIndex(index);
      setSubsetEndIndex(null);
      return;
    }

    setSubsetEndIndex(index);
  };

  const applyRouteSubsetToPlaylist = () => {
    if (
      hasPlaylistOwnGpx ||
      subsetStartIndex == null ||
      subsetEndIndex == null ||
      !Array.isArray(routePoints) ||
      routePoints.length < 2
    ) {
      return;
    }

    const subset = sliceGpxSubset(
      routePoints,
      subsetStartIndex,
      subsetEndIndex,
    );
    if (subset.length < 2) {
      toast.error("Selected GPX subset must include at least 2 points");
      return;
    }

    onChange({
      ...v,
      gpx_points: subset,
      gpxPoints: subset,
    });
    toast.success(`Copied ${subset.length} GPX points from route to playlist`);
  };

  const handleSingleGpxUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !parseUploadedGpx) return;

    setIsParsingGpx(true);
    try {
      const parsed = await parseUploadedGpx(file);
      const points = normalizeGpxPointsInput(parsed?.gpx_points || parsed);
      onChange({
        ...v,
        gpx_points: points,
        gpxPoints: points,
      });
      setLastGpxFile(file.name);
      toast.success("Playlist GPX imported");
    } catch {
      toast.error("Failed to parse GPX file");
    } finally {
      setIsParsingGpx(false);
      event.target.value = "";
    }
  };

  const handleSequenceUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || !parseUploadedGpxFiles) return;

    setIsParsingGpx(true);
    try {
      const uploadedFiles = await parseUploadedGpxFiles(files);
      const nextFiles = resequenceGpxFiles([
        ...(v.gpx_files || []),
        ...uploadedFiles,
      ]);
      onChange({
        ...v,
        gpx_files: nextFiles,
      });
      toast.success("GPX files imported");
    } catch {
      toast.error("Failed to parse one or more GPX files");
    } finally {
      setIsParsingGpx(false);
      event.target.value = "";
    }
  };

  const movePlaylistFile = (index, direction) => {
    const list = [...(v.gpx_files || [])];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    set("gpx_files", resequenceGpxFiles(list));
  };

  const removePlaylistFile = (index) => {
    const list = [...(v.gpx_files || [])];
    list.splice(index, 1);
    set("gpx_files", resequenceGpxFiles(list));
  };

  const maybeFetchPlaylistMetadata = async (rawURL, nextPlaylistValue) => {
    const trimmed = String(rawURL || "").trim();
    if (!trimmed || !fetchPlaylistMetadata) return;

    let parsed;
    try {
      parsed = new URL(trimmed);
    } catch {
      return;
    }

    const host = parsed.hostname.toLowerCase();
    const isYouTube =
      host === "youtube.com" ||
      host === "www.youtube.com" ||
      host === "m.youtube.com" ||
      host === "youtu.be";
    if (!isYouTube) return;

    setIsFetchingPlaylistMetadata(true);
    try {
      const metadata = await fetchPlaylistMetadata(trimmed);
      if (!metadata) return;

      // Respect manual entry by only filling missing values.
      onChange({
        ...nextPlaylistValue,
        youtube_playlist_url: trimmed,
        title: nextPlaylistValue.title || metadata.title || nextPlaylistValue.title,
      });
    } catch {
      // Ignore metadata lookup failures while the user types.
    } finally {
      setIsFetchingPlaylistMetadata(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      <div>
        <Label>Route</Label>
        <Select
          value={v.route_id || ""}
          onValueChange={(nextValue) => set("route_id", nextValue)}
          disabled={routeDisabled}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select route" />
          </SelectTrigger>
          <SelectContent>
            {routes.map((route) => (
              <SelectItem key={route.id} value={route.id}>
                {route.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Title</Label>
        <Input
          value={v.title || ""}
          onChange={(event) => set("title", event.target.value)}
        />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea
          value={v.description || ""}
          onChange={(event) => set("description", event.target.value)}
        />
      </div>
      <div>
        <Label>YouTube Playlist or Video URL (optional)</Label>
        <Input
          type="url"
          placeholder="https://www.youtube.com/playlist?list=..."
          value={v.youtube_playlist_url || ""}
          onChange={(event) => {
            const nextURL = event.target.value;
            const nextPlaylistValue = {
              ...v,
              youtube_playlist_url: nextURL,
            };
            onChange(nextPlaylistValue);
            maybeFetchPlaylistMetadata(nextURL, nextPlaylistValue);
          }}
          disabled={youtubePlaylistUrlDisabled}
        />
        <div className="text-xs text-[#5C665F] mt-2">
          {youtubePlaylistUrlDisabled
            ? "Locked after playlist creation."
            : isFetchingPlaylistMetadata
              ? "Fetching playlist metadata..."
              : "If a playlist is provided, videos are created automatically from the playlist in order"}
        </div>
      </div>
      {showStatusField && (
        <div>
          <Label>Status</Label>
          <Select
            value={String(v.status || "").trim() || "created"}
            onValueChange={(nextValue) => set("status", nextValue)}
          >
            <SelectTrigger data-testid="playlist-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {showCreatorFields && (
        <div>
          <Label>Creator</Label>
          <Select
            value={v.creator_id || "__none__"}
            onValueChange={(nextValue) => {
              if (nextValue === "__none__") {
                onChange({
                  ...v,
                  creator_id: "",
                  creator_name: "",
                  creator_url: "",
                });
                return;
              }
              const selectedCreator = creators.find(
                (creator) => creator.id === nextValue,
              );
              onChange({
                ...v,
                creator_id: nextValue,
                creator_name: selectedCreator?.name || "",
                creator_url: selectedCreator?.url || "",
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an existing creator (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No creator</SelectItem>
              {creators.map((creator) => (
                <SelectItem key={creator.id} value={creator.id}>
                  {creator.name}
                  {creator.url ? ` · ${creator.url}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {showCreatorFields && (
        <div>
          <Label>Creator Name</Label>
          <Input
            value={v.creator_name || ""}
            onChange={(event) =>
              onChange({
                ...v,
                creator_id: "",
                creator_name: event.target.value,
              })
            }
          />
        </div>
      )}
      {showCreatorFields && (
        <div>
          <Label>Creator Website URL</Label>
          <Input
            type="url"
            placeholder="https://creator.example.com"
            value={v.creator_url || ""}
            onChange={(event) =>
              onChange({
                ...v,
                creator_id: "",
                creator_url: event.target.value,
              })
            }
          />
        </div>
      )}

      <div className="rounded-xl border border-[#E5E0D8] bg-[#FCFAF6] p-3 space-y-3">
        {gpxUploadMode === "single" && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-[#1A1D1A]">
                  Playlist GPX
                </div>
                <div className="text-xs text-[#5C665F]">
                  {playlistOwnGpxPoints.length > 0
                    ? `Points: ${playlistOwnGpxPoints.length}`
                    : "No playlist GPX uploaded"}
                </div>
              </div>
              {playlistOwnGpxPoints.length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onChange({
                      ...v,
                      gpx_points: [],
                      gpxPoints: [],
                    });
                    setLastGpxFile("");
                  }}
                >
                  Clear GPX
                </Button>
              )}
            </div>

            <div>
              <Label>Upload playlist GPX (optional)</Label>
              <Input
                type="file"
                accept=".gpx,application/gpx+xml,application/xml,text/xml"
                onChange={handleSingleGpxUpload}
                disabled={isParsingGpx}
              />
              <div className="text-xs text-[#5C665F] mt-2">
                {isParsingGpx
                  ? "Parsing..."
                  : lastGpxFile
                    ? `last file: ${lastGpxFile}`
                    : ""}
              </div>
            </div>
          </>
        )}

        {allowRouteSubsetCopy && !hasPlaylistOwnGpx && (
          <>
            <div className="rounded-lg overflow-hidden border border-[#E5E0D8] h-56 bg-white">
              {Array.isArray(routePoints) && routePoints.length > 1 ? (
                <MapView
                  gpxPoints={routePoints}
                  markerSegment={selectedSubsetSegment}
                  onMapClick={handleSubsetMapPick}
                  height="100%"
                  fit
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-sm text-[#5C665F] px-4 text-center">
                  Select a route with GPX data to choose a playlist track
                  subset.
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
              <div>
                <Label>Start point index</Label>
                <Input value={subsetStartIndex ?? ""} readOnly />
              </div>
              <div>
                <Label>End point index</Label>
                <Input value={subsetEndIndex ?? ""} readOnly />
              </div>
              <Button
                type="button"
                size="sm"
                disabled={subsetStartIndex == null || subsetEndIndex == null}
                onClick={applyRouteSubsetToPlaylist}
              >
                Use selected route segment
              </Button>
            </div>
            <div className="text-xs text-[#5C665F]">
              Click once to set start, click again to set end. Clicking a third
              time starts a new selection. Source: route GPX.
            </div>
          </>
        )}

        {gpxUploadMode === "sequence" && (
          <div>
            <Label>GPX files (sequenced)</Label>
            <Input
              type="file"
              accept=".gpx,application/gpx+xml,application/xml,text/xml"
              multiple
              disabled={isParsingGpx}
              onChange={handleSequenceUpload}
            />
            <div className="text-xs text-[#5C665F] mt-2">
              {isParsingGpx
                ? "Parsing GPX files..."
                : `${(v.gpx_files || []).length} files in sequence`}
            </div>
            <div className="mt-3 space-y-2">
              {(v.gpx_files || []).map((file, index) => (
                <div
                  key={file.id || `${file.name}-${index}`}
                  className="flex items-center justify-between gap-2 border border-[#E5E0D8] rounded-lg px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">
                      {index + 1}. {file.name || `GPX ${index + 1}`}
                    </div>
                    <div className="text-xs text-[#5C665F]">
                      {file.point_count || (file.gpx_points || []).length} pts ·{" "}
                      {file.distance_km || 0} km
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => movePlaylistFile(index, -1)}
                      disabled={index === 0}
                    >
                      Up
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => movePlaylistFile(index, 1)}
                      disabled={index === (v.gpx_files || []).length - 1}
                    >
                      Down
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removePlaylistFile(index)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <PoiEditor
          title="Playlist map & POIs"
          gpxPoints={playlistDisplayPoints}
          secondaryGpxPoints={routePoints}
          pois={v.pois || []}
          onChange={(nextPois) => set("pois", nextPois)}
        />
      </div>
    </div>
  );
}
